import { useCallback, useEffect, useMemo, useRef, useState } from "react";

import {
  addBeltHistory,
  deleteBeltHistory,
  editBeltHistory,
  getBeltHistoryByAdherent,
  getBeltsList,
} from "../../../../services/beltServices";

import {
  getGroupsByUser,
  getGroupAdherents,
} from "../../../../services/groupServices";

import { useUser } from "../../../Home/UserContext";
import { useNotification } from "../../../../components/Notification/NotificationManager";

import {
  DEFAULT_BELT_FORM,
  DEFAULT_BULK_BELT_FORM,
  DEFAULT_FILTERS,
  buildCoachGradeStats,
  buildGradeRows,
  filterGradeRows,
  getUserId,
  mergeApprovedAdherentsById,
  normalizeBeltPayload,
  validateBeltForm,
} from "../utils/passageGradeCoachUtils";

export function usePassageGradeCoachPage() {
  const { user } = useUser();
  const { addNotification } = useNotification();

  const notificationRef = useRef(addNotification);
  const hasLoadedOnceRef = useRef(false);

  const [groups, setGroups] = useState([]);
  const [adherents, setAdherents] = useState([]);
  const [belts, setBelts] = useState([]);
  const [historiesByAdherent, setHistoriesByAdherent] = useState({});

  const [filters, setFilters] = useState(DEFAULT_FILTERS);

  const [selectedRow, setSelectedRow] = useState(null);
  const [selectedGrade, setSelectedGrade] = useState(null);
  const [beltForm, setBeltForm] = useState(DEFAULT_BELT_FORM);

  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [savingBelt, setSavingBelt] = useState(false);
  const [deletingGradeId, setDeletingGradeId] = useState(null);
  const [isHistoryOpen, setIsHistoryOpen] = useState(false);

  const userId = useMemo(() => getUserId(user), [user]);

  const [selectedAdherentIds, setSelectedAdherentIds] = useState([]);
  const [bulkBeltForm, setBulkBeltForm] = useState(DEFAULT_BULK_BELT_FORM);
  const [savingBulkBelt, setSavingBulkBelt] = useState(false);

  useEffect(() => {
    notificationRef.current = addNotification;
  }, [addNotification]);

  const rows = useMemo(() => {
    return buildGradeRows(adherents, historiesByAdherent);
  }, [adherents, historiesByAdherent]);

  const filteredRows = useMemo(() => {
    return filterGradeRows(rows, filters);
  }, [filters, rows]);

  const selectedRows = useMemo(() => {
    const selectedSet = new Set(selectedAdherentIds.map(String));

    return rows.filter((row) => selectedSet.has(String(row.adherent.id)));
  }, [rows, selectedAdherentIds]);

  const selectedFilteredRows = useMemo(() => {
    const selectedSet = new Set(selectedAdherentIds.map(String));

    return filteredRows.filter((row) =>
      selectedSet.has(String(row.adherent.id)),
    );
  }, [filteredRows, selectedAdherentIds]);

  const stats = useMemo(() => {
    return buildCoachGradeStats(rows);
  }, [rows]);

  const fetchCoachAdherents = useCallback(async () => {
    if (!userId) return [];

    const userGroups = await getGroupsByUser(userId);
    const safeGroups = Array.isArray(userGroups) ? userGroups : [];

    setGroups(safeGroups);

    const adherentEntries = await Promise.all(
      safeGroups.map(async (group) => {
        try {
          const response = await getGroupAdherents(group.id);
          const groupAdherents = Array.isArray(response?.adherents)
            ? response.adherents
            : [];

          return groupAdherents
            .filter((adherent) => adherent.status === "approved")
            .map((adherent) => ({
              ...adherent,
              group,
            }));
        } catch (error) {
          console.error(
            `Erreur lors du chargement des adhérents du groupe ${group.id} :`,
            error,
          );

          return [];
        }
      }),
    );

    const mergedAdherents = mergeApprovedAdherentsById(adherentEntries.flat());

    setAdherents(mergedAdherents);

    return mergedAdherents;
  }, [userId]);

  const fetchHistories = useCallback(async (targetAdherents = []) => {
    const entries = await Promise.all(
      targetAdherents.map(async (adherent) => {
        try {
          const history = await getBeltHistoryByAdherent(adherent.id);
          return [adherent.id, Array.isArray(history) ? history : []];
        } catch (error) {
          console.error(
            `Erreur lors du chargement de l'historique ceinture de l'adhérent ${adherent.id} :`,
            error,
          );

          return [adherent.id, []];
        }
      }),
    );

    const histories = Object.fromEntries(entries);

    setHistoriesByAdherent(histories);

    return histories;
  }, []);

  const fetchData = useCallback(
    async ({ silent = false } = {}) => {
      const isInitialLoad = !hasLoadedOnceRef.current;

      if (!userId) {
        setGroups([]);
        setAdherents([]);
        setHistoriesByAdherent({});
        setLoading(false);
        return;
      }

      if (isInitialLoad) {
        setLoading(true);
      } else {
        setRefreshing(true);
      }

      try {
        const [coachAdherents, beltList] = await Promise.all([
          fetchCoachAdherents(),
          getBeltsList(),
        ]);

        setBelts(Array.isArray(beltList) ? beltList : []);

        await fetchHistories(coachAdherents);

        if (!silent && !isInitialLoad) {
          notificationRef.current?.(
            "Passages de grades actualisés.",
            "success",
          );
        }
      } catch (error) {
        console.error("Erreur lors de la récupération des passages :", error);

        notificationRef.current?.(
          "Erreur lors de la récupération des passages de grades.",
          "error",
        );
      } finally {
        hasLoadedOnceRef.current = true;
        setLoading(false);
        setRefreshing(false);
      }
    },
    [fetchCoachAdherents, fetchHistories, userId],
  );

  useEffect(() => {
    fetchData({ silent: true });
  }, [fetchData]);

  const refreshSelectedRowHistory = useCallback(async (adherentId) => {
    if (!adherentId) return;

    const history = await getBeltHistoryByAdherent(adherentId);
    const safeHistory = Array.isArray(history) ? history : [];

    setHistoriesByAdherent((prev) => ({
      ...prev,
      [adherentId]: safeHistory,
    }));

    setSelectedRow((prev) => {
      if (!prev || Number(prev.adherent?.id) !== Number(adherentId)) {
        return prev;
      }

      return {
        ...prev,
        grades: safeHistory,
      };
    });
  }, []);

  const updateFilters = useCallback((name, value) => {
    setFilters((prev) => ({
      ...prev,
      [name]: value,
    }));
  }, []);

  const resetFilters = useCallback(() => {
    setFilters(DEFAULT_FILTERS);
  }, []);

  const openHistoryModal = useCallback((row) => {
    setSelectedRow(row);
    setSelectedGrade(null);
    setBeltForm(DEFAULT_BELT_FORM);
    setIsHistoryOpen(true);
  }, []);

  const closeHistoryModal = useCallback(() => {
    setIsHistoryOpen(false);
    setSelectedRow(null);
    setSelectedGrade(null);
    setBeltForm(DEFAULT_BELT_FORM);
  }, []);

  const handleBeltInputChange = useCallback((event) => {
    const { name, value } = event.target;

    setBeltForm((prev) => ({
      ...prev,
      [name]: value,
    }));
  }, []);

  const handleEditGrade = useCallback((grade) => {
    setSelectedGrade(grade);
    setBeltForm({
      belt_id: grade?.belt_id || "",
      date_passage:
        grade?.date_passage?.slice?.(0, 10) || grade?.date_passage || "",
    });
  }, []);

  const handleCancelGradeEdit = useCallback(() => {
    setSelectedGrade(null);
    setBeltForm(DEFAULT_BELT_FORM);
  }, []);

  const handleSubmitBelt = useCallback(
    async (event) => {
      event.preventDefault();

      if (!selectedRow?.adherent?.id) {
        notificationRef.current?.("Aucun judoka sélectionné.", "error");
        return;
      }

      const error = validateBeltForm(beltForm);

      if (error) {
        notificationRef.current?.(error, "error");
        return;
      }

      setSavingBelt(true);

      try {
        const payload = normalizeBeltPayload(beltForm, selectedRow.adherent.id);

        if (selectedGrade?.id) {
          await editBeltHistory(selectedGrade.id, payload);
          notificationRef.current?.(
            "Passage de grade modifié avec succès.",
            "success",
          );
        } else {
          await addBeltHistory(payload);
          notificationRef.current?.(
            "Passage de grade ajouté avec succès.",
            "success",
          );
        }

        setSelectedGrade(null);
        setBeltForm(DEFAULT_BELT_FORM);

        await refreshSelectedRowHistory(selectedRow.adherent.id);
      } catch (error) {
        console.error("Erreur lors de l'enregistrement du grade :", error);

        notificationRef.current?.(
          "Erreur lors de l'enregistrement du passage de grade.",
          "error",
        );
      } finally {
        setSavingBelt(false);
      }
    },
    [beltForm, refreshSelectedRowHistory, selectedGrade, selectedRow],
  );

  const handleDeleteGrade = useCallback(
    async (grade) => {
      if (!grade?.id || !selectedRow?.adherent?.id) return;

      const confirmDelete = window.confirm("Supprimer ce passage de grade ?");

      if (!confirmDelete) return;

      setDeletingGradeId(grade.id);

      try {
        await deleteBeltHistory(grade.id);

        notificationRef.current?.(
          "Passage de grade supprimé avec succès.",
          "success",
        );

        await refreshSelectedRowHistory(selectedRow.adherent.id);

        if (selectedGrade?.id === grade.id) {
          setSelectedGrade(null);
          setBeltForm(DEFAULT_BELT_FORM);
        }
      } catch (error) {
        console.error("Erreur lors de la suppression du grade :", error);

        notificationRef.current?.(
          "Erreur lors de la suppression du passage de grade.",
          "error",
        );
      } finally {
        setDeletingGradeId(null);
      }
    },
    [refreshSelectedRowHistory, selectedGrade, selectedRow],
  );

  const toggleSelectAdherent = useCallback((adherentId) => {
    setSelectedAdherentIds((prev) => {
      const id = String(adherentId);

      if (prev.map(String).includes(id)) {
        return prev.filter((currentId) => String(currentId) !== id);
      }

      return [...prev, adherentId];
    });
  }, []);

  const selectAllFilteredRows = useCallback(() => {
    setSelectedAdherentIds((prev) => {
      const existing = new Set(prev.map(String));

      filteredRows.forEach((row) => {
        existing.add(String(row.adherent.id));
      });

      return Array.from(existing);
    });
  }, [filteredRows]);

  const unselectAllFilteredRows = useCallback(() => {
    const filteredIds = new Set(
      filteredRows.map((row) => String(row.adherent.id)),
    );

    setSelectedAdherentIds((prev) =>
      prev.filter((id) => !filteredIds.has(String(id))),
    );
  }, [filteredRows]);

  const clearSelection = useCallback(() => {
    setSelectedAdherentIds([]);
    setBulkBeltForm(DEFAULT_BULK_BELT_FORM);
  }, []);

  const handleBulkBeltInputChange = useCallback((event) => {
    const { name, value } = event.target;

    setBulkBeltForm((prev) => ({
      ...prev,
      [name]: value,
    }));
  }, []);

  const handleSubmitBulkBelt = useCallback(
    async (event) => {
      event.preventDefault();

      if (!selectedRows.length) {
        notificationRef.current?.("Sélectionnez au moins un judoka.", "error");
        return;
      }

      const error = validateBeltForm(bulkBeltForm);

      if (error) {
        notificationRef.current?.(error, "error");
        return;
      }

      setSavingBulkBelt(true);

      try {
        await Promise.all(
          selectedRows.map((row) =>
            addBeltHistory(normalizeBeltPayload(bulkBeltForm, row.adherent.id)),
          ),
        );

        notificationRef.current?.(
          `Passage de grade ajouté pour ${selectedRows.length} judoka${
            selectedRows.length > 1 ? "s" : ""
          }.`,
          "success",
        );

        setSelectedAdherentIds([]);
        setBulkBeltForm(DEFAULT_BULK_BELT_FORM);

        await fetchData({ silent: true });
      } catch (error) {
        console.error("Erreur lors du passage collectif :", error);

        notificationRef.current?.(
          "Erreur lors de l'ajout collectif du passage de grade.",
          "error",
        );
      } finally {
        setSavingBulkBelt(false);
      }
    },
    [bulkBeltForm, fetchData, selectedRows],
  );

  return {
    groups,
    adherents,
    belts,
    rows,
    filteredRows,
    filters,
    stats,
    selectedRow,
    selectedGrade,
    beltForm,
    loading,
    refreshing,
    savingBelt,
    deletingGradeId,
    isHistoryOpen,

    selectedAdherentIds,
    selectedRows,
    selectedFilteredRows,
    bulkBeltForm,
    savingBulkBelt,

    fetchData,
    updateFilters,
    resetFilters,
    openHistoryModal,
    closeHistoryModal,
    handleBeltInputChange,
    handleEditGrade,
    handleCancelGradeEdit,
    handleSubmitBelt,
    handleDeleteGrade,

    toggleSelectAdherent,
    selectAllFilteredRows,
    unselectAllFilteredRows,
    clearSelection,
    handleBulkBeltInputChange,
    handleSubmitBulkBelt,
  };
}
